import type { Thunk, Action, Computed } from 'easy-peasy';
import { thunk, action, computed } from 'easy-peasy';
import type { Injections } from '../services/injections';
import type { TrackingRequest, TrackingResponse } from '../services/TrackingService/types';
import type { ApplicationErrorOptions } from '../../types';
import { store } from '..';
import { replace } from 'redux-first-history';
import qs from 'qs';
import parseStatusCode from '../errors/parseStatusCode';

export interface TrackingModel {
  trackings?: TrackingResponse[];
  lastAction?: number;
  trackingActionStart: Action<TrackingModel, void>;
  trackingSummaryList: Computed<TrackingModel, any>;
  getTrackingByShipiumTrackingId: Thunk<TrackingModel, string, Injections, Promise<TrackingResponse>>;
  getTrackingByCarrierTrackingId: Thunk<
    TrackingModel,
    { carrierTrackingId: string; carrierId: string },
    Injections,
    Promise<TrackingResponse>
  >;
  bulkTrackingSearch: Thunk<TrackingModel, TrackingRequest, Injections, Promise<TrackingResponse[]>>;
  bulkCarrierTrackingSearch: Thunk<TrackingModel, TrackingRequest, Injections, Promise<TrackingResponse[]>>;
  setTrackings: Action<TrackingModel, TrackingResponse[]>;
  setTrackingDetail: Action<TrackingModel, string>;
  selectedTracking?: TrackingResponse;
  setShipiumTracking: Action<
    TrackingModel,
    | { shipiumTrackingId: string; trackingResponse: TrackingResponse }
    | {
        carrierTrackingId: string;
        carrierId: string;
        trackingResponse: TrackingResponse;
      }
  >;
  shipiumTrackings: Record<string, TrackingResponse>;
  trackingError: Action<TrackingModel, ApplicationErrorOptions>;
}
export const trackingModel: TrackingModel = {
  shipiumTrackings: {},
  trackingSummaryList: computed((state) => {
    if (!state?.trackings) {
      return [];
    }
    return state?.trackings?.map((val: TrackingResponse) => {
      return {
        carrier: val.carrierId,
        carrierServiceMethodId: val.carrierServiceMethodId,
        trackingId: val.carrierTrackingId,
        status: val.shipmentStatus,
        shippedDateTime: val.shippedDateTime,
        deliveredDateTime: val.deliveredAtDateTime,
        carrierEstimatedDeliveryDate: val.carrierEstimatedDeliveryDate,
        labelPrintedDate: val.trackingEvents.find((e) => e.shipmentStatus === 'Label Printed')?.eventDate,
      };
    });
  }),
  trackingActionStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  setTrackingDetail: action((state, trackingId) => {
    const detail: TrackingResponse | undefined = state?.trackings?.find((val: any) => {
      return val.carrierTrackingId === trackingId;
    });
    if (detail) {
      state.selectedTracking = { ...detail };
    }
  }),
  getTrackingByShipiumTrackingId: thunk(async (actions, payload, { injections }) => {
    try {
      actions.trackingActionStart();
      const { trackingService } = injections;
      const trackingResponse = await trackingService.getTrackingByShipiumTrackingId(payload);
      actions.setShipiumTracking({
        shipiumTrackingId: payload,
        trackingResponse,
      });
      return trackingResponse;
    } catch (error) {
      actions.trackingError({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to fetch any tracking information for specified shipment.',
        asNotification: true,
      });
      throw error;
    }
  }),
  getTrackingByCarrierTrackingId: thunk(async (actions, payload, { injections }) => {
    try {
      actions.trackingActionStart();
      const { trackingService } = injections;
      const trackingResponse = await trackingService.getTrackingByCarrierTrackingId(
        payload.carrierTrackingId,
        payload.carrierId,
      );
      actions.setShipiumTracking({
        carrierTrackingId: payload.carrierTrackingId,
        carrierId: payload.carrierId,
        trackingResponse,
      });
      return trackingResponse;
    } catch (error) {
      actions.trackingError({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to fetch any tracking information for specified carrier.',
        asNotification: true,
      });
      throw error;
    }
  }),
  bulkTrackingSearch: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.trackingActionStart();
      const { trackingService } = injections;
      const trackingRes = await trackingService.bulkCarrierTrackingSearch(payload);
      const url = `${window.location.pathname}?${qs.stringify(payload)}`;
      store.dispatch(replace(url));
      actions.setTrackings(trackingRes);
      return trackingRes;
    } catch (error) {
      actions.trackingError({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to fetch any tracking information for specified list.',
        asNotification: true,
      });
      throw error;
    }
  }),
  bulkCarrierTrackingSearch: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.trackingActionStart();
      const { trackingService } = injections;
      const trackingRes = await trackingService.bulkCarrierTrackingSearch(payload);
      const url = `${window.location.pathname}?${qs.stringify(payload)}`;
      store.dispatch(replace(url));
      actions.setTrackings(trackingRes);
      return trackingRes;
    } catch (error) {
      actions.trackingError({
        error,
        httpCode: parseStatusCode(error),
        message: 'Failed to fetch any tracking information for specified list.',
        asNotification: true,
      });
      throw error;
    }
  }),
  trackingError: action((state, payload) => {
    console.log('error', payload);
  }),
  setTrackings: action((state, payload) => {
    state.trackings = payload;
  }),
  setShipiumTracking: action((state, payload) => {
    if ('carrierTrackingId' in payload) {
      state.shipiumTrackings[payload.carrierTrackingId] = payload.trackingResponse;
    } else {
      state.shipiumTrackings[payload.shipiumTrackingId] = payload.trackingResponse;
    }
  }),
};
